import { ReactNode } from "react";
import { Link } from "react-router-dom";

import { motion } from "framer-motion"; //  추가
import { useResponsiveMobile } from "../../../hooks/useResponsiveMobile";

const Card = ({
  title,
  content,
  src,
}: {
  title: string;
  content: React.ReactNode;
  src: string;
}) => {
  const isMobile = useResponsiveMobile(900);
  return (
    <div className="group md:h-[408px] h-[229px] flex flex-col justify-between rounded-[16px] border border-[#DDE0E6] bg-white p-[24px] transition-colors md:hover:bg-[#0079FF] overflow-hidden">
      <div className="flex flex-col gap-4 md:gap-[12px]">
        <p className="text-[18px] md:text-display3 font-bold text-[#121319] transition-colors md:group-hover:text-white">
          {title}
        </p>

        <div className="text-[14px] md:text-heading2  font-normal text-[#4D4F58] transition-colors md:group-hover:text-white break-keep">
          {content}
        </div>
      </div>

      <img
        loading="lazy"
        className="
        self-end
        transform-gpu
        transition-transform duration-300 ease-out
        md:group-hover:rotate-[5deg]
        md:group-hover:translate-x-[6px]
        md:group-hover:-translate-y-[4px]
        h-auto
      "
        src={src}
        width={isMobile ? 77 : 160}
        alt=""
        draggable={false}
      />
    </div>
  );
};

/**  모바일 카드 등장 애니메이션 wrapper (PC 영향 없음) */
const MobileInView = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{
      once: true,
      amount: 0.7, //  70% 이상 보일 때
      // margin: "0px 0px -10% 0px", // 필요하면 트리거 시점 미세조정
    }}
    transition={{ duration: 0.55, ease: "easeOut", delay }}
  >
    {children}
  </motion.div>
);

export const Section5 = () => {
  const isMobile = useResponsiveMobile(900);
  return (
    <section className="w-full flex justify-center md:px-[100px] lg:px-[120px] xl:px-[160px] md:pb-[160px] md:pt-[160px] pt-[80px] mb-[120px]">
      <div className="w-full flex-col gap-[120px] md:flex-row flex max-w-[1440px]">
        {/* left intro */}
        <div className="flex flex-col gap-6 items-center md:items-start justify-start shrink-0 ">
          <span className="font-normal text-[#0079FF] text-xl">품 소개</span>
          {isMobile ? (
            <span className="font-bold text-[#121319] text-[36px] text-center">
              품에 대해 더 자세히 <br />
              알고싶으세요?
            </span>
          ) : (
            <span className="font-bold text-[#121319] md:text-display">
              품에 대해 <br /> 더 자세히 <br />
              알고싶으세요?
            </span>
          )}
          <Link className="font-normal text-[#0079FF] text-xl" to="/features">
            자세히 보러가기&gt;
          </Link>
        </div>

        {/* right cards */}
        <section className="flex-1 flex justify-center">
          {isMobile ? (
            /*  MOBILE: 70% in-view 시 카드별 등장 */
            <div className="w-full flex flex-col gap-[24px] px-4">
              <MobileInView delay={0.0}>
                <Card
                  src="/assets/images/home/service1.png"
                  title="결제 / 정산 내역"
                  content={
                    <>
                      품(Poom)은 분산되어 있는 <br />
                      결제 데이터를 하나의 시스템으로 통합 지원합니다.
                    </>
                  }
                />
              </MobileInView>

              <MobileInView delay={0.05}>
                <Card
                  src="/assets/images/home/service2.png"
                  title="정산 서비스"
                  content={
                    <>
                      품(Poom)은 B2B 플랫폼 기업을 위한 <br />
                      정확한 정산 데이터 처리와 {!isMobile && <br />}
                      실시간 통합 보고서를 제공합니다.
                    </>
                  }
                />
              </MobileInView>

              <MobileInView delay={0.1}>
                <Card
                  src="/assets/images/home/service5.png"
                  title="가맹점 서비스"
                  content={
                    <>
                      결제내역 확인부터 입금현황을 실시간으로 확인하고, <br />
                      세금계산서·현금영수증 발행까지 가능합니다.
                    </>
                  }
                />
              </MobileInView>

              <MobileInView delay={0.15}>
                <Card
                  src="/assets/images/home/service4.png"
                  title="결제 서비스"
                  content={
                    <>
                      품(Poom)은 현장 중심의 <br /> 다양한 결제 방식을 지원합니다.
                    </>
                  }
                />
              </MobileInView>

              <MobileInView delay={0.2}>
                <Card
                  src="/assets/images/home/service3.png"
                  title="영업대행 관리 서비스"
                  content={
                    <>
                      다양한 영업 구조에 맞춰 영업 역할 별 <br />
                      계층관리를 지원하며 효율적인 관리 업무가 <br />
                      가능합니다.
                    </>
                  }
                />
              </MobileInView>
            </div>
          ) : (
            /*  PC */
            <div className="w-full max-w-[1144px] grid lg:grid-cols-2 md:grid-cols-1 gap-x-[32px] md:gap-[24px]">
              <div className="flex flex-col gap-[24px]">
                <Card
                  src="/assets/images/home/service1.png"
                  title="결제 / 정산 내역"
                  content={
                    <>
                      품(Poom)은 분산되어 있는 <br />
                      결제 데이터를 하나의 시스템으로 통합 지원합니다.
                    </>
                  }
                />
                <Card
                  src="/assets/images/home/service5.png"
                  title="가맹점 서비스"
                  content={
                    <>
                      결제내역 확인부터 입금현황을 실시간으로 확인하고, <br /> 세금계산서
                      증빙관리까지 가능합니다.
                    </>
                  }
                />
                <Card
                  src="/assets/images/home/service3.png"
                  title="영업대행 관리 서비스"
                  content={
                    <>
                      다양한 영업 구조에 맞춰 영업 역할 별 <br />
                      계층관리를 지원하며 효율적인 관리 업무가 가능합니다.
                    </>
                  }
                />
              </div>

              <div className="flex flex-col gap-[24px] lg:pt-[40%] ">
                <Card
                  src="/assets/images/home/service2.png"
                  title="정산 서비스"
                  content={
                    <>
                      품(Poom)은 B2B 플랫폼 기업을 위한 <br />
                      정확한 정산 데이터 처리와
                      <br />
                      실시간 통합 보고서를 제공합니다.
                    </>
                  }
                />
                <Card
                  src="/assets/images/home/service4.png"
                  title="결제 서비스"
                  content={<>품(Poom)은 현장 중심의 다양한 결제 방식을 지원합니다.</>}
                />
              </div>
            </div>
          )}
        </section>
      </div>
    </section>
  );
};
